/**
 * Calculate the distance between two geographic coordinates using the Haversine formula
 * @param lat1 Latitude of first point
 * @param lng1 Longitude of first point
 * @param lat2 Latitude of second point
 * @param lng2 Longitude of second point
 * @returns Distance in miles
 */
export function calculateDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 3959; // Earth's radius in miles
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
    Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) *
    Math.sin(dLng / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  
  return distance;
}

/**
 * Convert degrees to radians
 */
function toRad(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Sort items by distance from an origin point
 * @param items Array of items with lat/lng coordinates
 * @param origin Origin point with lat/lng coordinates
 * @returns Array of items sorted by distance with distance property added
 */
export function sortByDistance<T extends { lat?: number | null; lng?: number | null; latitude?: number | null; longitude?: number | null }>(
  items: T[],
  origin: { lat: number; lng: number }
): Array<T & { distance: number }> {
  return items
    .map(item => {
      // Handle both lat/lng and latitude/longitude property names
      const itemLat = item.lat ?? item.latitude;
      const itemLng = item.lng ?? item.longitude;
      
      // Skip items without valid coordinates
      if (itemLat == null || itemLng == null) {
        return { ...item, distance: Infinity };
      }
      
      const distance = calculateDistance(
        origin.lat,
        origin.lng,
        itemLat,
        itemLng
      );
      
      return { ...item, distance };
    })
    .sort((a, b) => a.distance - b.distance);
}

/**
 * Returns the distance unit for a given tenant country.
 * @param country ISO alpha-2 country code from TenantDataContext
 * @returns 'km' for Canada, 'mi' for all others
 */
export function distanceUnitForCountry(country?: string | null): 'mi' | 'km' {
  return country === 'CA' ? 'km' : 'mi';
}

/**
 * Format distance for display
 * @param miles Distance in miles (always pass miles internally)
 * @param unit Display unit — 'km' converts to kilometers, 'mi' stays in miles (default)
 * @returns Formatted string (e.g., "2.5 mi", "4.0 km", or "< 0.1 km")
 */
export function formatDistance(miles: number, unit: 'mi' | 'km' = 'mi'): string {
  if (miles === Infinity) return 'N/A';
  if (unit === 'km') {
    const km = miles * 1.60934;
    if (km < 0.1) return '< 0.1 km';
    return `${km.toFixed(1)} km`;
  }
  if (miles < 0.1) return '< 0.1 mi';
  return `${miles.toFixed(1)} mi`;
}











